import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  FormControl,
  AbstractControl,
  ValidationErrors,
  Validator,
  NG_VALIDATORS,
} from '@angular/forms';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  forwardRef,
  Output,
  EventEmitter,
  SimpleChanges,
  OnChanges,
  OnDestroy,
  Renderer2,
} from '@angular/core';
import { Observable, Subscription } from 'rxjs';

@Component({
  selector: 'irembogov-select',
  templateUrl: './irembo-select.component.html',
  styleUrls: ['./irembo-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => IremboSelectComponent),
      multi: true,
    },
  ],
})
export class IremboSelectComponent
  implements ControlValueAccessor, OnChanges, OnDestroy
{
  @Input() items: Record<string, unknown>[] | null = [];
  @Input() bindLabel = '';
  @Input() bindValue = '';
  @Input() compareWithFn: (a: any, b: any) => boolean = (a, b) => {
    if (this.bindValue) {
      return JSON.stringify(a?.[this.bindValue]) === JSON.stringify(b);
    }
    return JSON.stringify(a) === JSON.stringify(b);
  };
  @Input() placeholder = '';
  @Input() disabled = false;
  @Input() multiple = false;
  @Input() virtualScroll = false;
  @Input() labelForId: unknown;
  @Input() customFormControl = new FormControl();
  @Output() itemSelected: EventEmitter<any> = new EventEmitter<any>();
  @Input() loading = false;
  @Input() parent: string | undefined;
  @Input() hideIcon = false;
  @Input() showCancel = true;
  @Input() customClass = '';
  @Input() appendTo = 'body';
  @Input() dropdownPosition: 'bottom' | 'top' | 'auto' = 'auto';
  @Input() max: number | undefined;
  @Input() min: number | undefined;

  constructor(private renderer: Renderer2) {}

  /* eslint-disable @typescript-eslint/no-unused-vars, @typescript-eslint/no-empty-function*/
  private _onChange = (value: unknown) => {};
  private _onTouch = (value: unknown) => {};
  private itemsSubscription!: Subscription;

  /* eslint-enable */

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['items']?.currentValue) {
      if (this.itemsSubscription) {
        this.itemsSubscription.unsubscribe();
      }

      if (this.items instanceof Observable) {
        this.itemsSubscription = this.items.subscribe(items => {
          this.items = [...items];
          this.updateItemsDisabledState();
        });
      } else {
        this.items = [...changes['items'].currentValue];
        this.updateItemsDisabledState();
      }
    }
  }

  writeValue(obj: unknown): void {
    this.customFormControl.setValue(obj);
    // Update disabled state when value is set externally
    setTimeout(() => this.updateItemsDisabledState(), 0);
  }
  registerOnChange(fn: (_: unknown) => void): void {
    this._onChange = fn;
  }
  registerOnTouched(fn: (_: unknown) => void): void {
    this._onTouch = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  change(): void {
    let selectedItems: any[] = [];

    if (Array.isArray(this.customFormControl.value)) {
      selectedItems = [...this.customFormControl.value];
    }

    const errors: ValidationErrors = {};

    if (this.max !== undefined && selectedItems.length > this.max) {
      selectedItems.pop();
      this.customFormControl.setValue(selectedItems, { emitEvent: false });
      return;
    }

    if (this.min !== undefined && selectedItems.length < this.min) {
      errors['min'] = { requiredMin: this.min, actual: selectedItems.length };
    }

    if (Object.keys(errors).length) {
      this.customFormControl.setErrors(errors);
    } else {
      this.customFormControl.setErrors(null);
    }

    // Update disabled state of items when selection changes
    this.updateItemsDisabledState();

    this._onChange(this.customFormControl.value);
    this._onTouch(this.customFormControl.value);
    this.itemSelected.emit(this.customFormControl.value);
    this.customFormControl.updateValueAndValidity({ emitEvent: true });
  }

  private updateItemsDisabledState(): void {
    if (!this.multiple || this.max === undefined || !this.items) {
      return;
    }

    const currentValues = this.customFormControl.value || [];
    const isMaxReached = currentValues.length >= this.max;

    this.items = this.items.map(item => {
      const isSelected = currentValues.some((val: any) =>
        this.compareWithFn(item, val)
      );
      return {
        ...item,
        disabled: isMaxReached && !isSelected,
      };
    });
  }

  ngOnDestroy(): void {
    if (this.itemsSubscription) {
      this.itemsSubscription.unsubscribe();
    }
  }

  onDropDownOpen() {
    setTimeout(() => {
      if (this.customClass) {
        this.renderer.addClass(
          document.querySelector('.ng-dropdown-panel'),
          this.customClass
        );
      }
    }, 0);
  }
}
